<template>
  <div class="chat-title-list d-flex flex-column">
    <!-- Search + Filter -->
    <div class="pa-2 pb-1">
      <v-text-field
        v-model="search"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="use_language.search_name + ' chat...'"
      />
      <v-select
        v-if="availableTags.length > 0"
        v-model="filterTagId"
        :items="tagSelectItems"
        outlined
        dense
        hide-details
        clearable
        class="mt-2"
        :placeholder="use_language.filter_by + ' tag'"
        prepend-inner-icon="mdi-tag-outline"
      />
    </div>
    <v-divider />

    <!-- List -->
    <div
      ref="scrollEl"
      class="title-scroll flex-grow-1"
      @scroll="onScroll"
    >
      <div v-if="loading && titles.length === 0" class="py-4 text-center">
        <v-progress-circular indeterminate color="primary" size="24" />
      </div>

      <div
        v-else-if="filteredTitles.length === 0"
        class="py-6 text-center"
        style="color: grey"
      >
        <v-icon size="40" color="grey lighten-1">mdi-message-text-outline</v-icon>
        <div class="caption mt-1">{{ search || filterTagId ? use_language.no_matching_items : use_language.no_messages }}</div>
      </div>

      <v-list v-else dense class="py-0">
        <template v-for="item in filteredTitles">
          <v-list-item
            :key="item.chatID"
            :class="{ 'title-active': activeChatId === parseInt(item.chatID) }"
            @click="selectChat(item)"
          >
            <!-- Avatar + Platform badge + Shop badge (overlay) -->
            <div class="avatar-wrap mr-3">
              <v-avatar size="40">
                <v-img
                  v-if="item.platformSenderProfilePicture"
                  :src="item.platformSenderProfilePicture"
                />
                <v-icon v-else size="36" color="grey lighten-1">
                  mdi-account-circle
                </v-icon>
              </v-avatar>
              <!-- Platform logo (top-right) -->
              <span
                class="platform-badge"
                :style="{ background: platformColor(item.platformID) }"
                :title="platformLabel(item.platformID)"
              >
                <v-img
                  v-if="item.platformLogo"
                  :src="item.platformLogo"
                  width="14"
                  height="14"
                />
                <v-icon v-else size="10" color="white">
                  {{ platformIcon(item.platformID) }}
                </v-icon>
              </span>
              <!-- Shop profile (bottom-right) -->
              <v-avatar
                v-if="item.platformShopProfilePicture"
                size="16"
                class="shop-badge"
              >
                <v-img :src="item.platformShopProfilePicture" />
              </v-avatar>
            </div>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.platformSenderName || (item.platformName + "." + item.chatID) }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-truncate mt-1">
                {{ item.lastMessage || '...' }}
              </v-list-item-subtitle>
              <!-- tags row -->
              <div v-if="tagsFor(item.chatID).length > 0" class="mt-1">
                <v-chip
                  v-for="t in tagsFor(item.chatID)"
                  :key="t.id"
                  x-small
                  dark
                  :color="t.color"
                  class="mr-1"
                >
                  {{ t.name }}
                </v-chip>
              </div>
            </v-list-item-content>
            <div class="text-right">
              <v-badge
                v-if="item.unreadCount > 0"
                color="error"
                :content="item.unreadCount"
                inline
              />
              <div class="caption mt-1" style="color: grey">
                {{ formatTime(item.lastMessageDate) }}
              </div>
            </div>
          </v-list-item>
          <v-divider :key="'d-' + item.chatID" />
        </template>
      </v-list>

      <div v-if="loadingMore" class="py-2 text-center">
        <v-progress-circular indeterminate size="18" color="primary" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { expressService_dotnet } from "@/website/global";
import { token_api } from "@/website/backend/token";
import { PLATFORM_COLOR, PLATFORM_ICON, PLATFORM_NAME } from "./constants";
import { get_languages } from '@/website/global_function';

const PAGE_SIZE = 20;

export default {
  name: "ChatTitleList",
  props: {
    shopId: { type: [String, Number], required: true },
    platformConnectionID: { type: [String, Number], default: null },
    platformID: { type: [String, Number], default: null },
    activeChatId: { type: [String, Number], default: null },
    tagsByChat: { type: Object, default: () => ({}) }, // { [chatId]: [tag] }
    availableTags: { type: Array, default: () => [] },
  },
  data: () => ({
    use_language: {},
    titles: [],
    loading: false,
    loadingMore: false,
    eof: false,
    search: "",
    debouncedSearch: "",
    searchTimer: null,
    filterTagId: null,
  }),
  computed: {
    filteredTitles() {
      // search + tag filter ย้ายเป็น server-side แล้ว → ไม่ต้องทำ client-side filter อะไรอีก
      return this.titles;
    },
    tagSelectItems() {
      return this.availableTags.map((t) => ({ value: t.id, text: t.name }));
    },
  },
  watch: {
    platformConnectionID() {
      this.titles = [];
      this.eof = false;
      this.loadTitles(true);
    },
    search(val) {
      // debounce ป้องกันยิง API ทุก keystroke (400ms ดีกว่า 200 สำหรับ server-side)
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.debouncedSearch = val; }, 400);
    },
    debouncedSearch() {
      // search เปลี่ยน → reload จาก server (skip=0 + reset titles)
      this.titles = [];
      this.eof = false;
      if (this.platformConnectionID != null) this.loadTitles(true);
    },
    filterTagId() {
      // tag filter เปลี่ยน → reload จาก server
      this.titles = [];
      this.eof = false;
      if (this.platformConnectionID != null) this.loadTitles(true);
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
    if (this.platformConnectionID != null) this.loadTitles(true);
  },
  methods: {
    get_languages,
    platformColor(id) { return PLATFORM_COLOR[id] || "#9e9e9e"; },
    platformIcon(id) { return PLATFORM_ICON[id] || "mdi-account-circle"; },
    platformLabel(id) { return PLATFORM_NAME[id] || ""; },
    tagsFor(chatId) {
      return this.tagsByChat[chatId] || [];
    },
    formatTime(t) {
      if (!t) return "";
      const s = String(t);
      // ถ้าเป็น ISO → แสดงเฉพาะ HH:mm
      if (s.includes("T")) {
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          return d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
        }
      }
      return s;
    },
    async loadTitles(reset = false) {
      if (this.platformConnectionID == null) return;
      if (reset) this.loading = true;
      else this.loadingMore = true;
      try {
        const skip = reset ? 0 : this.titles.length;
        const res = await axios.post(
          expressService_dotnet + "CoreMessage/get-chat-title-asyncs",
          {
            shopId: this.shopId,
            platform: [
              {
                platformConnectionID: this.platformConnectionID,
                platformID: this.platformID,
              },
            ],
            skip,
            take: PAGE_SIZE,
            search: (this.debouncedSearch || "").trim() || null,
            tagIds: this.filterTagId ? [this.filterTagId] : null,
          },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + token_api } }
        );
        const data = (res.data && res.data.data) || [];
        if (reset) this.titles = data;
        else this.titles = this.titles.concat(data);
        if (data.length < PAGE_SIZE) this.eof = true;
      } catch (err) {
        this.$swal && this.$swal({
          icon: "error",
          title: this.use_language.load_chat_list_failed,
          text: err.message || this.use_language.error,
        });
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    onScroll(e) {
      if (this.eof || this.loadingMore) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.loadTitles(false);
      }
    },
    selectChat(item) {
      this.$emit("select", item);
    },

    // ===== Public method สำหรับ parent =====
    /** ถูกเรียกจาก WebSocket "New" event */
    handleNewMessage(socketPayload) {
      const chatId = parseInt(socketPayload.ChatId);
      const idx = this.titles.findIndex((t) => parseInt(t.chatID) === chatId);
      const old = idx >= 0 ? this.titles[idx] : null;
      const merged = {
        chatID: chatId,
        shopID: socketPayload.ShopId,
        platformConnectionID: socketPayload.ChatTitle && socketPayload.ChatTitle.PlatformConnectionID,
        platformShopName: old ? old.platformShopName : "",
        platformShopProfilePicture: socketPayload.ChannelProfile,
        platformID: socketPayload.ChatTitle && socketPayload.ChatTitle.PlatformID,
        platformName: old ? old.platformName : "",
        platformLogo: old ? old.platformLogo : "",
        platformSenderName: socketPayload.ChatTitle && socketPayload.ChatTitle.PlatformSenderName,
        platformSenderProfilePicture: socketPayload.ChatTitle && socketPayload.ChatTitle.PlatformSenderProfilePicture,
        lastMessage: socketPayload.ChatTitle && socketPayload.ChatTitle.LastMessage,
        lastMessageDate: socketPayload.CreatedDatetime,
        lastMessageID: socketPayload.MessageId,
        isLastMessageSentByCustomer: socketPayload.ChatTitle && socketPayload.ChatTitle.IsLastMessageSentByCustomer,
        referenceSourceID: socketPayload.ChannelSourceId,
        unreadCount:
          parseInt(this.activeChatId || 0) === chatId
            ? 0
            : ((socketPayload.ChatTitle && socketPayload.ChatTitle.UnreadCount) || 0),
      };
      if (idx >= 0) this.titles.splice(idx, 1);
      this.titles.unshift(merged);
    },
    /** ถูกเรียกจาก WebSocket "Read" event */
    handleMarkRead(socketPayload) {
      const chatId = parseInt(socketPayload.ChatId);
      const idx = this.titles.findIndex((t) => parseInt(t.chatID) === chatId);
      if (idx < 0) return;
      this.$set(this.titles[idx], "unreadCount", 0);
      if (socketPayload.ChatTitle) {
        this.titles[idx].platformSenderName = socketPayload.ChatTitle.PlatformSenderName;
        this.titles[idx].lastMessage = socketPayload.ChatTitle.LastMessage;
      }
    },
    /** เคลีย unread badge ของ chat ที่เพิ่งเปิด */
    clearUnread(chatId) {
      const idx = this.titles.findIndex((t) => parseInt(t.chatID) === parseInt(chatId));
      if (idx >= 0) this.$set(this.titles[idx], "unreadCount", 0);
    },
  },
};
</script>

<style scoped>
.chat-title-list {
  height: 100%;
  background: #fff;
}
.title-scroll {
  overflow-y: auto;
}
.title-active {
  background-color: #e3f2fd !important;
}

/* แต่ละแถวสูงขึ้น — เพิ่ม padding + min-height */
.chat-title-list ::v-deep .v-list-item {
  min-height: 50px;
  padding-top: 5px;
  padding-bottom: 5px;
}

/* Avatar overlay — แสดง platform logo + shop logo บน customer avatar */
.avatar-wrap {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}
.platform-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  overflow: hidden;
  z-index: 1;
}
.shop-badge {
  position: absolute !important;
  bottom: -2px;
  right: -2px;
  border: 2px solid #fff;
  z-index: 1;
}
</style>
